import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SuperAdminService, { ISuperAdminOrgUsageDetail } from '../../../../shared/services/super-admin/super-admin.service';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../../loading-page';
import { UsageDateFilter, UsageDateRange, defaultUsageRange } from './usage-date-filter';
import { UsageEventsByType, reportBreakdownItems, analyticsTabBreakdownItems, ssoProviderBreakdownItems } from './usage-events-by-type';
import { UsageStatCard, UsageBreakdownCard, UsageBreakdownList, UsageRateRow, UsageImportsList, UsageSettingsStatus, USAGE_ENTITIES } from './usage-ui';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';

const EVENT_LABELS: { [key: string]: string } = {
  'user.logged_in': 'Login',
  'import.uploaded': 'Import uploaded',
  'import.committed': 'Import committed',
  'report.generated': 'Report generated',
  'report.exported': 'Report exported',
  'report.viewed': 'Report viewed',
  'report.analytics_generated': 'Analytics report created',
  'dashboard.created': 'Dashboard created',
  'dashboard.viewed': 'Dashboard viewed',
  'analytics_dashboard.viewed': 'Analytics dashboard viewed',
  'hotspot.viewed': 'Hotspot analysis',
  'survey.created': 'Survey created',
  'survey.sent': 'Survey sent',
  'survey.response_received': 'Survey response',
  'emission.reclassified_to_activity': 'Reclassified to activity',
  'mfa.enabled': '2FA enabled',
  'sso.enabled': 'SSO enabled',
  'sso.used': 'SSO used',
};

const DOMAIN_LABELS: { [key: string]: string } = {
  supplier_spend_category: 'Supplier category',
  bill_line: 'Bill lines',
};

const formatDateTime = (value: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'Never';
  return date.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const SuperAdminUsageOrgPage = () => {
  const { id } = useParams<{ id: string }>();
  const superAdminService = new SuperAdminService();
  const [detail, setDetail] = useState<ISuperAdminOrgUsageDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [range, setRange] = useState<UsageDateRange>(defaultUsageRange());
  const [detailTab, setDetailTab] = useState(0);
  const [eventSelected, setEventSelected] = useState<string[]>([]);
  const [errorSelected, setErrorSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    superAdminService.getOrganisationUsageDetail(id, range.from, range.to)
      .then(d => { setDetail(d); setIsLoading(false); })
      .catch(err => { setIsLoading(false); console.log(err); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, range]);

  const kpis = detail ? [
    {
      label: detail.plan.status === 'TRIAL'
        ? `Free trial, ends ${formatDate(detail.plan.trialEndsAt)} (${detail.plan.trialDaysRemaining}d left)`
        : detail.plan.status === 'SUBSCRIBED' ? 'Subscription active' : 'No subscription or trial',
      value: (detail.plan.status === 'TRIAL' ? 'Trial' : detail.plan.status === 'SUBSCRIBED' ? 'Subscribed' : 'Expired') as number | string,
      icon: <CardMembershipOutlinedIcon />,
      accent: (detail.plan.status === 'TRIAL' ? 'warning' : detail.plan.status === 'SUBSCRIBED' ? 'success' : 'error') as 'warning' | 'success' | 'error',
      previous: undefined as number | undefined,
    },
    { label: `Logins (${detail.windowDays}d)`, value: detail.totals.logins as number | string, icon: <LoginOutlinedIcon />, accent: 'success' as const, previous: detail.totalsPrev.logins as number | undefined },
    { label: 'Signed in now', value: detail.signedInNow as number | string, icon: <PeopleAltOutlinedIcon />, accent: 'info' as const, previous: undefined as number | undefined },
    { label: `Events (${detail.windowDays}d)`, value: detail.totals.events as number | string, icon: <InsightsOutlinedIcon />, accent: 'info' as const, previous: detail.totalsPrev.events as number | undefined },
    { label: `Suggestions (${detail.windowDays}d)`, value: detail.totals.suggestionOutcomes as number | string, icon: <AutoAwesomeOutlinedIcon />, accent: 'primary' as const, previous: detail.totalsPrev.suggestionOutcomes as number | undefined },
    { label: 'Last active', value: formatDateTime(detail.lastActivityAt), icon: <AccessTimeOutlinedIcon />, accent: 'success' as const, previous: undefined as number | undefined },
    { label: 'Last login', value: formatDateTime(detail.lastLoginAt), icon: <LoginOutlinedIcon />, accent: 'primary' as const, previous: undefined as number | undefined },
  ] : [];

  const eventHeadCells: IHeadCell[] = [
    { id: 'event', numeric: false, label: 'Event', sortable: false },
    { id: 'when', numeric: false, label: 'When', sortable: false },
    { id: 'source', numeric: false, label: 'Source', sortable: false },
  ];
  const eventRows = (detail?.recentEvents || []).map((event, idx) => ({
    id: `${event.eventType}-${event.occurredAt}-${idx}`,
    event: EVENT_LABELS[event.eventType] || event.eventType,
    when: formatDateTime(event.occurredAt),
    source: event.source || '-',
  }));

  const errorHeadCells: IHeadCell[] = [
    { id: 'when', numeric: false, label: 'When', sortable: false },
    { id: 'statusCode', numeric: true, label: 'Status', sortable: false },
    { id: 'method', numeric: false, label: 'Method', sortable: false },
    { id: 'path', numeric: false, label: 'Path', sortable: false },
    { id: 'message', numeric: false, label: 'Message', sortable: false },
  ];
  const errorRows = (detail?.recentErrors || []).map((e, idx) => ({
    id: `${e.occurredAt}-${idx}`,
    when: formatDateTime(e.occurredAt),
    statusCode: e.statusCode,
    method: e.method || '',
    path: e.path || '',
    message: e.message || '',
  }));

  return (
    <PageOuterContainer>
      <PageHeading
        title={detail ? detail.name : 'Usage'}
        includeBackButton={true}
        backButtonLink='/super-admin/usage'
        backButtonText='Back to usage'
        hasTabs={false}
      />
      <Box sx={{ mb: 2 }}>
        <UsageDateFilter onChange={setRange} />
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={detailTab} onChange={(_, next) => setDetailTab(next)} aria-label="organisation usage tabs">
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Errors" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <LoadingPage pageReady={!isLoading}>
        {detail && (
          <>
            <CustomTabPanel value={detailTab} index={0}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {kpis.map(kpi => (
                  <Box key={kpi.label} sx={{ flex: '1 1 180px', minWidth: 160 }}>
                    <UsageStatCard label={kpi.label} value={kpi.value} icon={kpi.icon} accent={kpi.accent} previous={kpi.previous} />
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>Arrows show the change against the previous {detail.windowDays} days.</Typography>
              <Box sx={{ columnGap: 3, columnCount: { xs: 1, md: 2 }, mb: 1 }}>
                <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                  <UsageBreakdownCard title="Events by type">
                    <UsageEventsByType
                      events={detail.eventsByType}
                      breakdowns={{
                        'report.viewed': reportBreakdownItems(detail.reportsByType),
                        'analytics_dashboard.viewed': analyticsTabBreakdownItems(detail.analyticsDashboardByTab),
                        'sso.used': ssoProviderBreakdownItems(detail.ssoByProvider, 'sso.used'),
                        'sso.enabled': ssoProviderBreakdownItems(detail.ssoByProvider, 'sso.enabled'),
                      }}
                      labels={EVENT_LABELS}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>Click a row with an arrow (Reports viewed, Analytics dashboard viewed, SSO) to see the breakdown.</Typography>
                  </UsageBreakdownCard>
                </Box>
                <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                  <UsageBreakdownCard title="Imports by path">
                    <UsageImportsList items={detail.importsByPath || []} />
                  </UsageBreakdownCard>
                </Box>
                <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                  <UsageBreakdownCard title="Records added">
                    <UsageBreakdownList items={USAGE_ENTITIES.map(e => ({ label: e.label, count: (detail.entitiesAdded as any)[e.key] || 0, previous: (detail.entitiesAddedPrev as any)[e.key] || 0 }))} />
                  </UsageBreakdownCard>
                </Box>
                <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                  <UsageBreakdownCard title="Settings enabled">
                    <UsageSettingsStatus settings={detail.settings as any} />
                  </UsageBreakdownCard>
                </Box>
                <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                  <UsageBreakdownCard title="Suggestion acceptance">
                    {detail.suggestionAcceptance.length === 0
                      ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>No suggestion outcomes in this window.</Typography>
                      : <Stack spacing={1}>
                        {detail.suggestionAcceptance.map(item => (
                          <UsageRateRow key={item.domain} label={DOMAIN_LABELS[item.domain] || item.domain} detail={`${item.accepted}/${item.total} (${item.acceptanceRate}%)`} percent={item.acceptanceRate} />
                        ))}
                      </Stack>}
                  </UsageBreakdownCard>
                </Box>
              </Box>

              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>Recent activity</Typography>
              <TableDisplay
                headCells={eventHeadCells}
                rows={eventRows}
                selected={eventSelected}
                setSelected={setEventSelected}
                perPage={25}
              />
            </CustomTabPanel>

            <CustomTabPanel value={detailTab} index={1}>
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>{detail.totals.errors} error{detail.totals.errors === 1 ? '' : 's'} in range</Typography>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <UsageBreakdownCard title="By status code">
                    <UsageBreakdownList
                      items={detail.errorsByStatus.map(row => ({ label: String(row.statusCode), count: row.count }))}
                      emptyText="No errors in this window."
                    />
                  </UsageBreakdownCard>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>Recent errors</Typography>
              <TableDisplay
                headCells={errorHeadCells}
                rows={errorRows}
                selected={errorSelected}
                setSelected={setErrorSelected}
                perPage={25}
              />
            </CustomTabPanel>
          </>
        )}
      </LoadingPage>
    </PageOuterContainer>
  );
};
